<template>
  <!-- Note that we use 'opacity' instead of v-show to ensure there are no hiccups in the font rendering process. -->
  <div
    class="text-blob ma-n2 pa-2 d-flex"
    :class="`align-${blob.text.valign || 'center'}`"
    v-bind:style="{ opacity: (loadFontStatus.pending || !didRender) ? 0 : 1 }"
  >

    <div
      ref="text-readonly"
      class="renderable-text"
      v-bind:class="{ hide: isContentEditable && isSelected && !hasMultiSelected && (!indirectEdit || isFocused) }"
      v-bind:style="blob.text.css || {}"
    />

    <!-- You added the 'key' because you were seeing issues where the element didn't actually get removed from the DOM when 'isContentEditable' changed, particularly with toggling on smart text -->
    <div
      v-if="isContentEditable"
      :key="isContentEditable"
      ref="text-editable"
      class="absolute renderable-text"
      v-bind:class="{
        'user-select-auto': isSelected || mode == 'view',
        hide: !isSelected || hasMultiSelected || (indirectEdit && !isFocused)
      }"
      v-bind:style="blob.text.css || {}"
      contenteditable spellcheck="false"
      @input="onInput"
      @focus="onFocusText"
      @blur="onBlurText"
      @paste="onPasteText"
      @keydown="onKeyDown"
      @dragstart.stop.prevent
    />

    <!-- Note that we explicitly handle mousedown / mouseup and touchstart / touchend so we can use our custom CLICK_THRESHOLD_MS to ignore long clicks -->
    <div
      v-if="isEditable && isSelected && !hasMultiSelected && indirectEdit && !isFocused"
      class="text-blob-fill"
      @mousedown="onClickStart"
      @mouseup="onClickEnd"
      @touchstart="onClickStart"
      @touchend="onClickEnd"
    />
  </div>
</template>

<style scoped>
.text-blob {
  width: calc(100% + 16px);
  height: calc(100% + 16px);
  transition: opacity .15s;
}
.renderable-text {
  line-height: 1.5;
  outline: none;
  line-break: normal;
  white-space: pre-wrap;
}
.renderable-text.hide {
  opacity: 0;
}
.text-blob-fill {
  position: absolute;
  inset: -4px;
}
</style>

<style>
sup {
  position: inherit;
  vertical-align: super;
}

.text-blob a {
  color: inherit !important;
  pointer-events: auto;
}

@keyframes font-effect-fire {
  0% {
    text-shadow: 0 -0.05em 0.2em #FFF, 0.01em -0.02em 0.15em #FE0, 0.01em -0.05em 0.15em #FC0, 0.02em -0.15em 0.2em #F90, 0.04em -0.20em 0.3em #F70,0.05em -0.25em 0.4em #F70, 0.06em -0.2em 0.9em #F50, 0.1em -0.1em 1.0em #F40;
  }
  25% {
    text-shadow: 0 -0.05em 0.2em #FFF, 0 -0.05em 0.17em #FE0, 0.04em -0.12em 0.22em #FC0, 0.04em -0.13em 0.27em #F90, 0.05em -0.23em 0.33em #F70, 0.07em -0.28em 0.47em #F70, 0.1em -0.3em 0.8em #F50, 0.1em -0.3em 0.9em #F40;
  }
  50% {    text-shadow: 0 -0.05em 0.2em #FFF, 0.01em -0.02em 0.15em #FE0, 0.01em -0.05em 0.15em #FC0, 0.02em -0.15em 0.2em #F90, 0.04em -0.20em 0.3em #F70,0.05em -0.25em 0.4em #F70, 0.06em -0.2em 0.9em #F50, 0.1em -0.1em 1.0em #F40;
  }
  75% {
    text-shadow: 0 -0.05em 0.2em #FFF, 0 -0.06em 0.18em #FE0, 0.05em -0.15em 0.23em #FC0, 0.05em -0.15em 0.3em #F90, 0.07em -0.25em 0.4em #F70, 0.09em -0.3em 0.5em #F70, 0.1em -0.3em 0.9em #F50, 0.1em -0.3em 1.0em #F40;
  }
  100% {
    text-shadow: 0 -0.05em 0.2em #FFF, 0.01em -0.02em 0.15em #FE0, 0.01em -0.05em 0.15em #FC0, 0.02em -0.15em 0.2em #F90, 0.04em -0.20em 0.3em #F70,0.05em -0.25em 0.4em #F70, 0.06em -0.2em 0.9em #F50, 0.1em -0.1em 1.0em #F40;
  }
}

@keyframes font-effect-stripes {
  from {
    background-position: 0px;
  }
  to {
    background-position: 1000px;
  }
}

@keyframes font-effect-shimmer {
  to {
    background-position-x: 100%;
  }
}

/* Ok, we have a problem. Text rendering varies by browser. We're using specific fonts, but we generally don't import a separate BOLD version of any of these fonts, leaving browsers to apply their OWN bold styling. In Chrome, bold styling does not change the width of the text, but in Safari and Firefox, it does. This can causes wrapping issues.
There are two possible solutions:
- Import specific versions of every font for regular, bold, regular italic, and bold italic (although note that regular italic doesn't seem to have the same problem)
- Create our own pseudo-bold styling using text-stroke. The problem with this is that it prevents the browser from recognizing that the text is bolded, which means that you cannot un-bold text. Since this is an obscure option not readily presented in the interface, and you can easily work around that by deleting the blob and recreating it, we're going to go with that.
*/
/*
.text-input b,
.text-input strong {
  color: inherit;
  font-weight: normal;
  text-stroke: 2px currentColor;
  -webkit-text-stroke: 2px currentColor;
}
*/
</style>

<script>
import constraints from '@/card-geometry-constraints.js';
import { shouldSaveBlob } from '@/utils/edit-order.js';
import { EMAIL_REGEX } from '@/utils/email.js';
import { injectLinksHTML } from '@/utils/external-content.js';
import loadFont from '@/utils/load-font.js';
import { resizeTextEl } from '@/utils/render-text.js';

const CLICK_THRESHOLD_TIMESTAMP_MS = 300;
const CLICK_THRESHOLD_MOVE_PX = 6;

const { W, H } = constraints.page;
const { MIN_H, MAX_W, MAX_H } = constraints.blob.TEXT;

function getRootEvent(e) {
  // Returns an object from which we can extract properties like 'screenX' and 'screenY'
  if (e.type == 'touchstart')
    return e.touches[0];
  if (e.type == 'touchend')
    return e.changedTouches[0];
  return e;
}

/*
TODO: figure out a cross-platform way to test if the keyboard is visible - listening for focus and checking the activeElement is NOT a reliable way to do it, i.e. the use case where a text blob exists but is not selected, then a user double-taps it. Note that that particular bug may have something to do with the second tap getting processed as a mouse event instead of a touch event...

TODO: you should also use this function in place of the 'virtual-keyboard' event and 'virtualKeyboard' variable that you currently use in the card-editor.

It seems like people mostly go the route of watching for screen resize events, though they're handled differently on Android vs iOS. From a Stack Overflow comment:
Android Chrome shrinks the viewport height to make room for the keyboard, so the page resizes when the keyboard is shown. iOS Safari overlays the page with the keyboard (page size stays the same), and changes how scrolling works. Safari both scrolls the page inside the viewport, and concurrently moves the viewport, ensuring that the bottom of the page is above the keyboard when scrolled all the way down.

https://stackoverflow.com/questions/28272274/how-to-detect-keyboard-show-hide-event-in-jquery-for-mobile-web-application/28272408

https://stackoverflow.com/questions/2593139/ipad-web-app-detect-virtual-keyboard-using-javascript-in-safari/19464062
*/

const LINK_REGEX = new RegExp(/(((ht|f)tps?:\/\/)|(\w[-+.%'~\w]*@))?([\w_-]+(?:(?:\.[\w_-]+)+))([\w.,@?^=%&:\/~+#-]*[\w@?^=%&\/~+#-])/g);
const LINK_TLDS = new Set([ 'com', 'net', 'edu', 'org', 'gov', 'info', 'xyz', 'site', 'me', 'co', 'us', 'uk', 'au', 'nz', 'eu', 'io', 'ai' ]);


// You had noticed in the past that Safari applies different properties based on whether or not the 'contenteditable'
// property is set. You had added the '-webkit-nbsp-mode: space;' setting in the scoped 'text-input' CSS class to
// account for that, but that of course means that &nbsp; characters wouldn't work as intended. I don't remember what
// behavior had caused you to do that in the first place, but if you find that you do need to set that property again,
// then you will need this function to actually replace &nbsp; characters with a span that has the same effect.
// function replaceNBSP(v) {
//   return (v || '').replace(/&nbsp;/g, '<span style="white-space: nowrap;"> </span>');
// }

export default {
  name: 'text-blob',

  props: [ 'blob', 'mode', 'isEditable', 'isSelected', 'isTransformInProgress', 'hasMultiSelected', 'indirectEdit', 'noLinks' ],

  data() {
    return {
      isFocused: false,
      didRender: false,
      loadFontStatus: this.$track('loadFont'),
      renderStatus: this.$track('render')
    };
  },

  computed: {
    renderCondition() {
      const b = this.blob;
      const t = b.text;
      return [ this.isContentEditable, t.text, t.font, t.color, t.size, t.fixed_size, t.align, t.valign, t.css, b.w, b.h ];
    },

    isContentEditable() {
      const { event_link } = this.blob.text;
      return this.isEditable && !this.hasMultiSelected && (!this.indirectEdit || this.isSelected) && (!event_link || event_link.disabled);
    },

    // isFitToPage() {
    //   const b = this.blob;
    //   return b.x == W / 2 && b.w == MAX_W && b.y == H / 2 && b.h == MAX_H;
    // },

    selectTextOnFocus() {
      return (!this.blob.user_id && (this.mode != 'design')) || (this.blob.text.text == 'Message here');
    }
  },

  created() {
    Object.assign(this, { MAX_H });
  },

  mounted() {
    // If an ancestor is set to 'display: none', then this element may have a reported 'scrollWidth' of 0, which will
    // cause the 'resizeTextEl' function to simply return the max size. Because of how this component is used, we can
    // trust that the 'scrollWidth' will either be 0 or its actual value, so we only have to use the ResizeObserver
    // until we actually have a reported value.
    if (!this.$el.scrollWidth) {
      this._resizeObserver = new ResizeObserver(
        () => {
          if (this.$el.scrollWidth) {
            this.render();
            this._resizeObserver?.disconnect();
          }
        }
      );
      this._resizeObserver.observe(this.$el);
    }
  },

  beforeDestroy() {
    this._resizeObserver?.disconnect();
  },

  methods: {
    onClickStart(e) {
      this._lastClick = e;
    },

    onClickEnd(e) {
      if (!this._lastClick)
        return;
      if (e.timeStamp - this._lastClick.timeStamp > CLICK_THRESHOLD_TIMESTAMP_MS)
        return;
      // Note: you may want to skip the position check for locked blobs
      const last = getRootEvent(this._lastClick);
      const cur = getRootEvent(e);
      if ([ 'screenX', 'screenY' ].some(x => Math.abs(cur[x] - last[x]) > CLICK_THRESHOLD_MOVE_PX))
        return;
      this.$emit('request-focus');
    },

    async loadFont() {
      await loadFont(this.blob.text.font);
      await this.$nextTick();
    },

    async focus(select) {
      const el = this.$refs['text-editable'];
      el.focus();
      if (select || this.selectTextOnFocus) {
        await this.$nextTick();
        await this.renderStatus.promise;
        getSelection().selectAllChildren(this.$refs['text-editable']);
      }
    },

    onInput() {
      // For block-level contenteditable elements, browsers will insert a newline character into the innerText instead
      // of leaving it empty
      // https://stackoverflow.com/questions/14638887/br-is-inserted-into-contenteditable-html-element-if-left-empty
      const el = this.$refs['text-editable'];
      this.blob.text.text = (el.innerText == '\n') ? '' : el.innerHTML;
    },

    clearDefaultText() {
      // User started typing without clearing the placeholder text (let's clear it for them)
      if (this.isContentEditable && (this.blob.text.text == 'Message here')) {
        // Note the distinction between 'innerText' and 'innerHTML', the former will strip any invisible tags like
        // '<b>' or '<i>''
        this.$refs['text-editable'].innerHTML = '';
        this.onInput();
      }
    },

    onKeyDown(e) {
      if (e.keyCode != 27)
        e.stopPropagation();
      this.clearDefaultText();
    },

    async onFocusText() {
      this.isFocused = true;
      this.$emit('focus-text');
      if (this.selectTextOnFocus) {
        await this.$nextTick();
        await this.renderStatus.promise;
        getSelection().selectAllChildren(this.$refs['text-editable']);
      }

      // Insane but effective trick to determine if a virtual keyboard is visible in iOS (both Chrome and Safari).
      // For Android devices, the window explicitly resizes, which means the normal scroll-handling mechanisms are
      // sufficient.
      // https://stackoverflow.com/a/2601085/8439453
      // if (!IS_MOBILE)
      //   return;
      // const el = document.documentElement;
      // el.scrollTop = 10;
      // if (el.scrollTop > 0)
      //   this.$emit('focus');
      // el.scrollTop = 0;
    },

    onBlurText() {
      this.isFocused = false;
      this.$emit('blur-text');
      getSelection().removeAllRanges();
    },

    onPasteText(e) {
      // We have a special handler here because we actually allow pasting in a text blob to capture things like
      // stickers / GIFs from mobile device keyboards.
      const plainText = e.clipboardData.getData('text/plain');
      if (plainText) {
        // Note that 'plainText' actually captures things like image URLs as well
        e.preventDefault();
        e.stopPropagation();

        this.clearDefaultText();

        // https://stackoverflow.com/questions/2920150/insert-text-at-cursor-in-a-content-editable-div
        const selection = getSelection();
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(plainText));

        this.onInput();
      }
    },

    fixScroll() {
      // A user can enter enough text to push the height of the inner element beyond the max height. Even though we
      // have overflow hidden, this still causes the element to scroll, so we fix that here. The consequence is a
      // little "flicker".
      setTimeout(() => this.$el.scrollTop = 0);
    },

    async render() {
      this.fixScroll();

      await this.$nextTick();
      await this.loadFont();

      const b = this.blob;
      const t = b.text;
      const opts = {
        color: t.color,
        font: t.font,
        font_size: t.size,
        min_font_size: constraints.blob.TEXT.MIN_SIZE,
        size: b,
        justify: t.align,
        align: t.valign
      };
      if (t.fixed_size)
        opts.min_font_size = t.size;

      const editable = this.$refs['text-editable'];
      if (editable) {
        // Do not inject links, render text directly into the content-editable field
        if (editable.innerHTML != t.text)
          // Note that we're careful not to alter the HTML unless necessary to avoid repositionoing the cursor
          editable.innerHTML = t.text;
        resizeTextEl(editable, opts, 0, true, !this.didRender);
      }

      const readonly = this.$refs['text-readonly'];
      if (!readonly)
        return;
      readonly.innerHTML = (this.isSelected || this.noLinks) ? t.text : injectLinksHTML(t.text);
      resizeTextEl(readonly, opts, 0, true, !this.didRender);

      // We need to keep track of the first time we auto-scaled text because it also applies the styles from the
      // 'opts' above, and we don't want to show the text at all without those styles.
      this.didRender = true;

      if (!this.isLoaded) {
        if (!window.NO_TRANSITIONS)
          // Make sure to wait for the opacity transition on the 'text-blob' class
          await new Promise(r => setTimeout(r, 150));

        this.isLoaded = true;
        this.$emit('loaded');
      }
    }
  },

  watch: {
    renderCondition: {
      immediate: true,
      handler() {
        this.render();
      }
    }
  }
}
</script>
